import React, { useState, useEffect } from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Grid,
  Typography,
} from "@mui/material";
import MDButton from "components/MDButton";
import { THEMES } from "lib/constants";

export default function WidgetList({ widgets, widgetDefinitions, setWidgetList }) {
  const [leftChecked, setLeftChecked] = useState([]);
  const [rightChecked, setRightChecked] = useState([]);
  const [state, setState] = useState({ widgetList: widgets, widgetDefinitionList: [] });

  const handleToggle = (value, checked, setChecked) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const computeWidgetDefinitionList = (widgetList) => {
    const definitionList = widgetDefinitions.filter(
      (def) => !(def.singleInstanceOnly && widgetList.some((w) => w.widgetId === def.id))
    );
    return definitionList;
  };

  const getNewWidgetSuffix = (w) => {
    try {
      const sameComponents = state.widgetList.filter(
        (widget) => widget.componentName === w.componentName
      );
      if (sameComponents.length) {
        return Math.max(...sameComponents.map((widget) => widget.gridKey.split("_")[2])) + 1;
      }
      return 0;
    } catch {
      return 0;
    }
  };

  const handleAddWidget = () => {
    let bottomWidget;
    if (state.widgetList.length) {
      bottomWidget = state.widgetList.reduce((max, widget) =>
        max.dataGrid.y > widget.dataGrid.y ? max : widget
      );
      bottomWidget = state.widgetList
        .filter((w) => w.dataGrid.y === bottomWidget.dataGrid.y)
        .reduce((max, widget) => (max.dataGrid.x > widget.dataGrid.x ? max : widget));
    }
    const newWidgets = rightChecked.map((index) => {
      const definition = state.widgetDefinitionList[index];
      const suffix = getNewWidgetSuffix(definition);

      // Compute x, y
      let x = 0;
      let y = 0;
      if (bottomWidget) {
        y = bottomWidget.dataGrid.y;
        if (bottomWidget.dataGrid.w > 1 || bottomWidget.dataGrid.x === 1 || definition.width > 1) {
          y += bottomWidget.dataGrid.h;
        }
        if (
          y === bottomWidget.dataGrid.y &&
          bottomWidget.dataGrid.x === 0 &&
          definition.width === 1
        ) {
          x = 1;
        }
      }

      const obj = {
        widgetId: definition.id,
        componentName: definition.componentName,
        gridKey: `${definition.componentName}_${definition.id}_${suffix}`,
        singleInstanceOnly: definition.singleInstanceOnly,
        dataSource: definition.dataSource,
        integrationId: definition.FK_integrations_id,
        displayName: definition.displayName,
        widgetTitle: definition.widgetTitle,
        isCustomizable: definition.isCustomizable,
        dataGrid: {
          x,
          y,
          w: definition.width,
          h: definition.height,
          ...(definition.minWidth && { minW: definition.minWidth }),
          ...(definition.minHeight && { minH: definition.minHeight }),
          ...(definition.maxWidth && { maxW: definition.maxWidth }),
          ...(definition.maxHeight && { maxH: definition.maxHeight }),
          isDraggable: definition.isDraggable,
          isResizable: definition.isResizable,
          isBounded: true,
        },
      };

      bottomWidget = obj;
      return obj;
    });
    const newArray = [...state.widgetList, ...newWidgets];
    setState({
      widgetList: newArray,
      widgetDefinitionList: computeWidgetDefinitionList(newArray),
    });
    setRightChecked([]);
    setWidgetList(newArray);
  };

  const handleRemoveWidget = () => {
    const remainingWidgets = state.widgetList.filter((_, index) => !leftChecked.includes(index));

    setState({
      widgetList: remainingWidgets,
      widgetDefinitionList: computeWidgetDefinitionList(remainingWidgets),
    });
    setLeftChecked([]);
    setWidgetList(remainingWidgets);
  };

  // const getDisplayName = (widgetId) =>
  //   widgetDefinitions.find((definition) => definition.id === widgetId)?.displayName ||
  //   "Unknown Widget";

  const getWidgetDisplayName = (widget) => {
    if (!widget.displayName) return "Unknown Widget";
    if (widget.kpiName && !widget.title) return `${widget.displayName} - ${widget.kpiName}`;
    if (widget.kpiName && widget.title === widget.displayName)
      return `${widget.displayName} - ${widget.kpiName}`;
    return widget.title ? `${widget.displayName} - ${widget.title}` : widget.displayName;
  };

  useEffect(() => {
    const definitions = computeWidgetDefinitionList(state.widgetList);
    setState({
      ...state,
      widgetDefinitionList: definitions,
    });
  }, []);

  return (
    <Grid container spacing={2} pb={5} sx={{ position: "relative" }}>
      <Grid item xs={6} pr={2}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 400,
            fontSize: "1rem",
            color: THEMES.PRIMARY.GREEN,
          }}
        >
          Active Widgets
        </Typography>
        <List sx={{ width: "100%" }}>
          {state.widgetList.map((item, i) => {
            const labelId = item.gridKey;
            return (
              <ListItem key={item.gridKey} disablePadding>
                <ListItemButton
                  onClick={handleToggle(i, leftChecked, setLeftChecked)}
                  dense
                  disableRipple
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={leftChecked.indexOf(i) !== -1}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ "aria-labelledby": labelId }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    id={labelId}
                    primary={getWidgetDisplayName(item)}
                    sx={{
                      ml: {
                        xs: "-20px",
                        sm: "0",
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        <MDButton
          variant="text"
          color="gray60"
          disabled={leftChecked.length === 0}
          sx={{
            px: 1,
            "&:hover": {
              color: "red",
            },
            position: "absolute",
            bottom: 0,
          }}
          onClick={handleRemoveWidget}
        >
          Remove from dashboard
        </MDButton>
      </Grid>
      <Grid item xs={6} sx={{ borderLeft: "1px dotted #888" }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 400,
            fontSize: "1rem",
            color: "#d7741f",
          }}
        >
          Available Widgets
        </Typography>
        <List sx={{ width: "100%" }}>
          {state.widgetDefinitionList.map((item, i) => {
            const labelId = item.id;
            return (
              <ListItem key={item.id} disablePadding>
                <ListItemButton
                  onClick={handleToggle(i, rightChecked, setRightChecked)}
                  dense
                  disableRipple
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={rightChecked.indexOf(i) !== -1}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ "aria-labelledby": labelId }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    id={labelId}
                    primary={item.displayName}
                    sx={{
                      ml: {
                        xs: "-20px",
                        sm: "0",
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        <MDButton
          variant="text"
          color="gray60"
          disabled={rightChecked.length === 0}
          sx={{
            px: 1,
            "&:hover": {
              color: "cornflowerBlue",
            },
            position: "absolute",
            bottom: 0,
            ml: {
              xs: 1,
              sm: 0,
            },
          }}
          onClick={handleAddWidget}
        >
          Add to dashboard
        </MDButton>
      </Grid>
    </Grid>
  );
}
